import api from './api';

export interface Notification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  is_read: boolean;
  created_at: string;
  read_at?: string;
  action_url?: string;
  metadata?: any;
  actor_name: string;
  is_recent: boolean;
  content_object_name?: string;
}

export interface NotificationPreferences {
  patient_notifications: boolean;
  consultation_notifications: boolean;
  appointment_notifications: boolean;
  invoice_notifications: boolean;
  stock_notifications: boolean;
  system_notifications: boolean;
  browser_notifications: boolean;
  email_notifications: boolean;
  email_frequency: 'instant' | 'hourly' | 'daily' | 'weekly';
}

export interface NotificationStats {
  total_count: number;
  unread_count: number;
  read_count: number;
  type_statistics: Record<string, number>;
}

class NotificationService {
  private baseURL = '/notifications/notifications';
  private prefsURL = '/notifications/preferences';

  // Obtenir toutes les notifications
  async getNotifications(filters?: {
    is_read?: boolean;
    type?: string;
    priority?: string;
  }): Promise<Notification[]> {
    const params = new URLSearchParams();

    if (filters?.is_read !== undefined) {
      params.append('is_read', filters.is_read.toString());
    }
    if (filters?.type) {
      params.append('type', filters.type);
    }
    if (filters?.priority) {
      params.append('priority', filters.priority);
    }

    const response = await api.get(`${this.baseURL}/`, { params });
    return response.data.results || response.data;
  }

  // Obtenir le nombre de notifications non lues
  async getUnreadCount(): Promise<number> {
    const response = await api.get(`${this.baseURL}/unread_count/`);
    return response.data.unread_count;
  }

  // Obtenir les notifications récentes
  async getRecentNotifications(): Promise<Notification[]> {
    const response = await api.get(`${this.baseURL}/recent/`);
    return response.data;
  }

  // Marquer une notification comme lue
  async markAsRead(notificationId: number): Promise<void> {
    await api.patch(`${this.baseURL}/${notificationId}/mark_as_read/`, {});
  }

  // Marquer plusieurs notifications comme lues
  async markMultipleAsRead(notificationIds: number[]): Promise<void> {
    await api.patch(`${this.baseURL}/mark_multiple_as_read/`, {
      notification_ids: notificationIds
    });
  }

  // Marquer toutes les notifications comme lues
  async markAllAsRead(): Promise<void> {
    await api.patch(`${this.baseURL}/mark_multiple_as_read/`, {
      mark_all: true
    });
  }

  // Supprimer les notifications lues
  async clearReadNotifications(): Promise<number> {
    const response = await api.delete(`${this.baseURL}/clear_read/`);
    return response.data.deleted_count;
  }

  // Obtenir les statistiques des notifications
  async getStats(): Promise<NotificationStats> {
    const response = await api.get(`${this.baseURL}/stats/`);
    return response.data;
  }

  // Obtenir les préférences de notification
  async getPreferences(): Promise<NotificationPreferences> {
    const response = await api.get(`${this.prefsURL}/`);
    return response.data;
  }

  // Mettre à jour les préférences de notification
  async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    const response = await api.patch(`${this.prefsURL}/1/`, preferences);
    return response.data;
  }

  // Créer une notification (pour les tests)
  async createNotification(notification: {
    notification_type: string;
    title: string;
    message: string;
    priority?: string;
    action_url?: string;
    metadata?: any;
  }): Promise<Notification> {
    const response = await api.post(`${this.baseURL}/`, notification);
    return response.data;
  }
}

export const notificationService = new NotificationService();
export default notificationService;